import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
}));

/**
 * The heading row, which carries the `aside` when there is one.
 *
 * Baseline-aligned rather than centred: the aside is a figure and the label is
 * a heading, and two different type sizes sitting on the same baseline read as
 * one line instead of two things in a box.
 */
const SectionHead = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: theme.spacing(1),
}));

/**
 * A titled group of controls.
 *
 * The auto panels are a flat run of five number fields, and "On win", "Bets"
 * and "Stop on loss" are three different kinds of thing: one changes the next
 * stake, one ends the run, one is a counter. Grouping them under a heading is
 * what tells the player which lever they are pulling.
 */
const PanelSection = ({
  label,
  hint,
  aside,
  children,
}: {
  label: string;
  /** One line under the controls, for what the numbers mean. */
  hint?: ReactNode;
  /**
   * A figure beside the heading - how full a keno card is, how much of a limit
   * is left.
   *
   * Beside the label rather than inside the controls because it is a readout of
   * the group as a whole, and a counter wedged in among the controls reads as
   * one of them.
   */
  aside?: ReactNode;
  children: ReactNode;
}) => (
  <Stack spacing={1}>
    {aside ? (
      <SectionHead>
        <SectionLabel>{label}</SectionLabel>
        {aside}
      </SectionHead>
    ) : (
      <SectionLabel>{label}</SectionLabel>
    )}
    {children}
    {hint ? (
      <Typography variant="caption" color="text.disabled">
        {hint}
      </Typography>
    ) : null}
  </Stack>
);

export default PanelSection;
